import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://parsdigitals.com';

const publicRoutes = {
  '/': { key: 'home', canonical: '/' },
  '/products': { key: 'products', canonical: '/products' },
  '/products/agents': { key: 'agents', canonical: '/products/agents' },
  '/agents': { key: 'agents', canonical: '/products/agents' },
  '/products/automation': { key: 'automation', canonical: '/products/automation' },
  '/products/apps': { key: 'apps', canonical: '/products/apps' },
  '/help': { key: 'help', canonical: '/help' },
  '/docs': { key: 'docs', canonical: '/docs' },
  '/guides': { key: 'guides', canonical: '/guides' },
  '/contact': { key: 'contact', canonical: '/contact' },
  '/about': { key: 'about', canonical: '/about' },
  '/privacy': { key: 'privacy', canonical: '/privacy' },
  '/terms': { key: 'terms', canonical: '/terms' },
  '/cookies': { key: 'cookies', canonical: '/cookies' },
};

const privateRoutes = {
  '/login': 'account',
  '/signup': 'account',
  '/signup/success': 'account',
  '/auth/callback': 'account',
  '/forgot-password': 'account',
  '/reset-password': 'account',
  '/dashboard': 'dashboard',
  '/admin': 'dashboard',
};

const setMeta = (selector, value) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    const match = selector.match(/meta\[(name|property)="([^"]+)"\]/);
    if (match) element.setAttribute(match[1], match[2]);
    document.head.appendChild(element);
  }
  element.setAttribute('content', value);
};

const setCanonical = (url) => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', url);
};

const getRoute = (pathname) => {
  if (pathname.startsWith('/agent/')) return publicRoutes['/products/agents'];
  if (privateRoutes[pathname]) return { key: privateRoutes[pathname], canonical: pathname, noIndex: true };
  return publicRoutes[pathname] || { key: 'notFound', canonical: pathname, noIndex: true };
};

const RouteMetadata = () => {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const route = getRoute(pathname);
    const title = t(`metadata.${route.key}.title`);
    const description = t(`metadata.${route.key}.description`);
    const canonicalUrl = new URL(route.canonical, SITE_URL).href;
    const locale = i18n.resolvedLanguage === 'en' ? 'en_US' : 'tr_TR';

    document.title = title;
    document.documentElement.lang = i18n.resolvedLanguage || 'tr';
    setCanonical(canonicalUrl);
    setMeta('meta[name="description"]', description);
    setMeta('meta[name="robots"]', route.noIndex ? 'noindex, nofollow' : 'index, follow');
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta('meta[property="og:locale"]', locale);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);
  }, [i18n.resolvedLanguage, pathname, t]);

  return null;
};

export default RouteMetadata;
